import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import { inventoryMovementService } from '@/services/inventoryMovementService'

/** Line shape for `inventoryTransferLinesJson` + `itemList[n].*` (matches Inventory Transfer / movement Swagger). */
export type InventoryDispatchSaveLine = {
  id: number
  transferId: number
  itemId: number
  batchId: number
  fromWarehouseId: number
  toWarehouseId: number
  previousQuantity: number
  quantityTransfered: number
  newQuantity: number
  isDeleted: string
}

export type InventoryDispatchSavePayload = {
  id: number
  code: string
  sourceWarehouseId: number
  sourceBranchId: number
  destinationWarehouseId: number
  destinationBranchId: number
  movementDate: string
  movementType: number
  approvedManager: string
  remarks: string
  attachmentFilePath: string
  branchId: number
  /** API: ReceivingStatus (1 Pending, 2 Transferred, 3 Received, 4 Cancel) */
  receivingStatus: number
  attachmentFile?: File | null
  itemList: InventoryDispatchSaveLine[]
}

/**
 * Multipart save for Inventory Dispatch (InventoryTransferBranch).
 * Does not send: ReceivingFilePath, ReceivingAttachmentFile, ReceivingDate (unless added later).
 */
export class InventoryDispatchService {
  private appendFormField(formData: FormData, key: string, value: unknown) {
    if (value === undefined || value === null) {
      formData.append(key, '')
      return
    }
    formData.append(key, String(value))
  }

  /** Same property order as inventory movement `inventoryTransferLinesJson`. */
  private buildInventoryTransferLinesJson(lines: InventoryDispatchSaveLine[]): string {
    const normalized = lines.map((l) => ({
      previousQuantity: l.previousQuantity,
      toWarehouseId: l.toWarehouseId,
      transferId: l.transferId,
      newQuantity: l.newQuantity,
      itemId: l.itemId,
      isDeleted: l.isDeleted,
      fromWarehouseId: l.fromWarehouseId,
      quantityTransfered: l.quantityTransfered,
      batchId: l.batchId,
      id: l.id,
    }))
    return JSON.stringify(normalized)
  }

  private logOutboundPayload(payload: InventoryDispatchSavePayload, inventoryTransferLinesJson: string) {
    const { attachmentFile, itemList, ...master } = payload
    console.log('[InventoryDispatch] Save — master (multipart text fields)', master)
    console.log('[InventoryDispatch] Save — inventoryTransferLinesJson (form field value)', inventoryTransferLinesJson)
    console.log('[InventoryDispatch] Save — lines (parsed)', itemList)
    console.log(
      '[InventoryDispatch] Save — AttachmentFile',
      attachmentFile ? { name: attachmentFile.name, size: attachmentFile.size, type: attachmentFile.type } : null,
    )
  }

  private buildSaveFormData(payload: InventoryDispatchSavePayload, inventoryTransferLinesJson: string): FormData {
    const formData = new FormData()

    this.appendFormField(formData, 'Id', payload.id)
    this.appendFormField(formData, 'Code', payload.code)
    this.appendFormField(formData, 'SourceWarehouseId', payload.sourceWarehouseId)
    this.appendFormField(formData, 'SourceBranchId', payload.sourceBranchId)
    this.appendFormField(formData, 'DestinationWarehouseId', payload.destinationWarehouseId)
    this.appendFormField(formData, 'DestinationBranchId', payload.destinationBranchId)
    this.appendFormField(formData, 'MovementDate', payload.movementDate)
    this.appendFormField(formData, 'MovementType', payload.movementType)
    this.appendFormField(formData, 'ApprovedManager', payload.approvedManager)
    this.appendFormField(formData, 'Remarks', payload.remarks)
    this.appendFormField(formData, 'AttachmentFilePath', payload.attachmentFilePath)

    if (payload.attachmentFile) {
      formData.append('AttachmentFile', payload.attachmentFile)
    } else {
      this.appendFormField(formData, 'AttachmentFile', '')
    }

    this.appendFormField(formData, 'BranchId', payload.branchId)
    this.appendFormField(formData, 'ReceivingStatus', payload.receivingStatus)

    payload.itemList.forEach((line, index) => {
      this.appendFormField(formData, `itemList[${index}].id`, line.id)
      this.appendFormField(formData, `itemList[${index}].transferId`, line.transferId)
      this.appendFormField(formData, `itemList[${index}].itemId`, line.itemId)
      this.appendFormField(formData, `itemList[${index}].batchId`, line.batchId)
      this.appendFormField(formData, `itemList[${index}].fromWarehouseId`, line.fromWarehouseId)
      this.appendFormField(formData, `itemList[${index}].toWarehouseId`, line.toWarehouseId)
      this.appendFormField(formData, `itemList[${index}].previousQuantity`, line.previousQuantity)
      this.appendFormField(formData, `itemList[${index}].quantityTransfered`, line.quantityTransfered)
      this.appendFormField(formData, `itemList[${index}].newQuantity`, line.newQuantity)
      this.appendFormField(formData, `itemList[${index}].isDeleted`, line.isDeleted)
    })

    formData.append('inventoryTransferLinesJson', inventoryTransferLinesJson)

    return formData
  }

  /** Paginated list of dispatch records for the current branch. Used by the
   *  Inventory Dispatch list page. */
  async getAll(
    branchId: number,
    pageNumber: number,
    pageSize: number,
  ): Promise<{ items: InventoryDispatchListRow[]; totalCount: number }> {
    const res = await apiClient.get<unknown>(
      endpoints.inventoryTransferBranch.getAllInventoryTransfers(branchId, pageSize, pageNumber),
    )
    const payload = unwrapResponse(res?.data)
    const arr: unknown[] = Array.isArray(payload)
      ? (payload as unknown[])
      : Array.isArray((payload as any)?.items)
        ? (payload as any).items
        : Array.isArray((payload as any)?.data)
          ? (payload as any).data
          : Array.isArray((payload as any)?.result)
            ? (payload as any).result
            : []
    const totalCountCandidates = [
      (payload as any)?.totalCount,
      (payload as any)?.totalRecords,
      (payload as any)?.recordCount,
      (payload as any)?.count,
      (payload as any)?.pagination?.total,
    ]
    let totalCount = arr.length
    for (const c of totalCountCandidates) {
      const n = Number(c)
      if (Number.isFinite(n)) {
        totalCount = n
        break
      }
    }

    const num = (v: unknown) => {
      const n = Number(v)
      return Number.isFinite(n) ? n : 0
    }
    const str = (v: unknown) => (v == null ? '' : String(v))

    type RawRow = {
      id: number
      code: string
      dispatchDate: string
      sourceWarehouseId: number
      destinationWarehouseId: number
      sourceBranchId: number
      destinationBranchId: number
      srcCode: string
      srcName: string
      destCode: string
      destName: string
      receivingStatus: number
    }
    const rawRows: RawRow[] = arr.map((raw) => {
      const r = raw as Record<string, unknown>
      return {
        id: num(r.id ?? r.Id ?? r.transferId ?? r.TransferId),
        code: str(r.code ?? r.Code),
        dispatchDate: str(
          r.movementDate ?? r.MovementDate ?? r.dispatchDate ?? r.DispatchDate ?? r.transferDate ?? r.TransferDate,
        ),
        sourceWarehouseId: num(r.sourceWarehouseId ?? r.SourceWarehouseId),
        destinationWarehouseId: num(r.destinationWarehouseId ?? r.DestinationWarehouseId),
        sourceBranchId: num(r.sourceBranch ?? r.SourceBranch ?? r.sourceBranchId ?? r.SourceBranchId),
        destinationBranchId: num(
          r.destinationBranch ?? r.DestinationBranch ?? r.destinationBranchId ?? r.DestinationBranchId,
        ),
        srcCode: str(r.sourceWarehouseCode ?? r.SourceWarehouseCode),
        srcName: str(r.sourceWarehouseName ?? r.SourceWarehouseName),
        destCode: str(r.destinationWarehouseCode ?? r.DestinationWarehouseCode),
        destName: str(r.destinationWarehouseName ?? r.DestinationWarehouseName),
        receivingStatus: num(r.receivingStatus ?? r.ReceivingStatus),
      }
    })

    // The API does not include warehouse names; fetch warehouses per unique
    // branch (source + destination) and build an `id -> "code — name"` map.
    const branchIds = new Set<number>()
    for (const r of rawRows) {
      if (r.sourceBranchId > 0) branchIds.add(r.sourceBranchId)
      if (r.destinationBranchId > 0) branchIds.add(r.destinationBranchId)
    }
    // Fall back to the calling branch if rows didn't include branch info.
    if (branchIds.size === 0 && branchId > 0) branchIds.add(branchId)

    const warehouseLabelById = new Map<number, string>()
    await Promise.all(
      Array.from(branchIds).map(async (bId) => {
        try {
          const warehouses = await inventoryMovementService.getWarehousesByBranchIdTransferBranch(bId)
          for (const w of warehouses) {
            if (!w?.id || warehouseLabelById.has(w.id)) continue
            const label = w.code && w.name ? `${w.code} — ${w.name}` : w.name || w.code || String(w.id)
            warehouseLabelById.set(w.id, label)
          }
        } catch (err) {
          console.warn('[InventoryDispatch] getAll: failed to resolve warehouses for branch', bId, err)
        }
      }),
    )

    const labelFromRaw = (code: string, name: string, id: number): string => {
      if (code && name) return `${code} — ${name}`
      if (name) return name
      if (code) return code
      const resolved = warehouseLabelById.get(id)
      if (resolved) return resolved
      return id > 0 ? String(id) : ''
    }

    const items: InventoryDispatchListRow[] = rawRows.map((r) => ({
      id: r.id,
      code: r.code,
      dispatchDate: r.dispatchDate,
      sourceWarehouse: labelFromRaw(r.srcCode, r.srcName, r.sourceWarehouseId),
      destinationWarehouse: labelFromRaw(r.destCode, r.destName, r.destinationWarehouseId),
      receivingStatus: r.receivingStatus,
    }))

    return { items, totalCount }
  }

  async save(payload: InventoryDispatchSavePayload): Promise<void> {
    const inventoryTransferLinesJson = this.buildInventoryTransferLinesJson(payload.itemList)
    this.logOutboundPayload(payload, inventoryTransferLinesJson)
    const formData = this.buildSaveFormData(payload, inventoryTransferLinesJson)
    await apiClient.post(endpoints.inventoryTransferBranch.save, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  }

  async delete(id: number): Promise<void> {
    const res = await apiClient.post(endpoints.inventoryTransferBranch.delete(id))
    const payload = res?.data as { result?: { isError?: boolean; msg?: string }; isError?: boolean; msg?: string } | undefined
    const result = payload?.result ?? payload
    if (result?.isError) {
      throw new Error(result.msg || 'Failed to delete inventory dispatch.')
    }
  }

  /** Fetches the master record (with `attachmentFilePath` etc.) for the
   *  given dispatch id. Used by the edit form to render the saved
   *  attachment as a clickable "View" link, similar to inventory movements. */
  async getById(id: number): Promise<InventoryDispatchMasterDto | null> {
    try {
      const res = await apiClient.get<unknown>(endpoints.inventoryTransferBranch.getById(id))
      const data = unwrapResponse(res?.data)
      if (!data || typeof data !== 'object') return null
      const d = data as Record<string, unknown>
      console.log('[InventoryDispatch] GetById raw payload:', d)
      const num = (v: unknown) => {
        const n = Number(v)
        return Number.isFinite(n) ? n : 0
      }
      const str = (v: unknown) => (v == null ? '' : String(v))
      return {
        id: num(d.id ?? d.Id ?? d.transferId ?? d.TransferId),
        code: str(d.code ?? d.Code),
        sourceWarehouseId: num(
          d.sourceWarehouseId ??
            d.SourceWarehouseId ??
            d.fromWarehouseId ??
            d.FromWarehouseId,
        ),
        sourceBranchId: num(
          d.sourceBranch ??
            d.SourceBranch ??
            d.sourceBranchId ??
            d.SourceBranchId ??
            d.fromBranchId ??
            d.FromBranchId,
        ),
        destinationWarehouseId: num(
          d.destinationWarehouseId ??
            d.DestinationWarehouseId ??
            d.destWarehouseId ??
            d.DestWarehouseId ??
            d.toWarehouseId ??
            d.ToWarehouseId ??
            d.targetWarehouseId ??
            d.TargetWarehouseId,
        ),
        destinationBranchId: num(
          d.destinationBranch ??
            d.DestinationBranch ??
            d.destinationBranchId ??
            d.DestinationBranchId ??
            d.destBranchId ??
            d.DestBranchId ??
            d.toBranchId ??
            d.ToBranchId ??
            d.targetBranchId ??
            d.TargetBranchId,
        ),
        movementDate: str(d.movementDate ?? d.MovementDate ?? d.dispatchDate ?? d.DispatchDate),
        movementType: num(d.movementType ?? d.MovementType),
        approvedManager: str(d.approvedManager ?? d.ApprovedManager),
        remarks: str(d.remarks ?? d.Remarks),
        attachmentFilePath: str(
          d.attachmentFilePath ??
            d.AttachmentFilePath ??
            d.attachmentFile ??
            d.AttachmentFile ??
            d.attachmentUrl ??
            d.AttachmentUrl ??
            ''
        ),
        branchId: num(d.branchId ?? d.BranchId),
        receivingStatus: num(d.receivingStatus ?? d.ReceivingStatus),
      }
    } catch (err) {
      console.warn('[InventoryDispatch] getById failed', err)
      return null
    }
  }

  /** Lines for an existing dispatch (used to repopulate the items grid). */
  async getLinesByTransferId(id: number): Promise<InventoryDispatchSaveLine[]> {
    try {
      const res = await apiClient.get<unknown>(endpoints.inventoryTransferBranch.getLinesByTransferId(id))
      const data = unwrapResponse(res?.data)
      const arr = Array.isArray(data)
        ? data
        : Array.isArray((data as any)?.items)
          ? (data as any).items
          : Array.isArray((data as any)?.data)
            ? (data as any).data
            : []
      const num = (v: unknown) => {
        const n = Number(v)
        return Number.isFinite(n) ? n : 0
      }
      return arr.map((row: any) => ({
        id: num(row.id ?? row.Id),
        transferId: num(row.transferId ?? row.TransferId),
        itemId: num(row.itemId ?? row.ItemId),
        batchId: num(row.batchId ?? row.BatchId),
        fromWarehouseId: num(row.fromWarehouseId ?? row.FromWarehouseId),
        toWarehouseId: num(row.toWarehouseId ?? row.ToWarehouseId),
        previousQuantity: num(row.previousQuantity ?? row.PreviousQuantity),
        quantityTransfered: num(row.quantityTransfered ?? row.QuantityTransfered),
        newQuantity: num(row.newQuantity ?? row.NewQuantity),
        isDeleted: String(row.isDeleted ?? row.IsDeleted ?? 'false'),
      }))
    } catch (err) {
      console.warn('[InventoryDispatch] getLinesByTransferId failed', err)
      return []
    }
  }
}

export type InventoryDispatchListRow = {
  id: number
  code: string
  dispatchDate: string
  sourceWarehouse: string
  destinationWarehouse: string
  receivingStatus: number
}

export type InventoryDispatchMasterDto = {
  id: number
  code: string
  sourceWarehouseId: number
  sourceBranchId: number
  destinationWarehouseId: number
  destinationBranchId: number
  movementDate: string
  movementType: number
  approvedManager: string
  remarks: string
  attachmentFilePath: string
  branchId: number
  receivingStatus: number
}

function unwrapResponse(payload: unknown): unknown {
  if (payload && typeof payload === 'object') {
    const p = payload as Record<string, unknown>
    if ('data' in p) return p.data
    if ('result' in p) return p.result
  }
  return payload
}

export const inventoryDispatchService = new InventoryDispatchService()
